import { isElectronApp } from '#~/electron/utils.js';
import { StudioError } from '#~/error/index.js';
import ThemeModule from '@studio/design-system/themes';
import { Lazy } from '@studio/js-utils/async';
import type { T_Any } from '@tryiris-ai/js-types';
import { SetEndpoint } from '@tryiris-ai/mailroom-helper/backend';
import type { Config } from './types.js';
import { toConfig } from './utils.js';

/**
 * ENG-3760 (G4): derive a per-channel localStorage key so concurrent
 * Iris-itg and Iris-prod renderers loaded from the same `file://` origin
 * (Electron packaged path) do not stomp on each other's hash env. The
 * channel name lives in `IRIS_ENV` here (the IPC `get-env-config` handler
 * in studio/electron/src/main/ipc.ts maps electron's `IRIS_APP_ENV` →
 * the renderer-facing `IRIS_ENV`). Falls back to the legacy unscoped key
 * when the channel is unknown so existing single-channel installs keep
 * reading what they already wrote.
 * Per rule: think-in-systems (ai.knowledge/global).
 */
function hashEnvKey(channel: string | undefined): string {
  return channel ? `__hash_env_${channel}` : '__hash_env';
}

/**
 * Lazily loaded configuration.
 *
 * @see https://vitejs.dev/guide/env-and-mode.html
 * @see https://vitejs.dev/config/shared-options.html#define
 */
const __config = new Lazy((): Config => {
  let hashEnv = {};

  if (isElectronApp()) {
    try {
      const parsed = window.electron?.ipcRenderer?.sendSync('get-env-config');

      // ENG-3760 (G4): `sendSync` is optional-chained and the IPC result may be
      // empty, so bail before writing rather than persisting the string
      // "undefined" under the hash-env key (which later reads would fail to parse).
      if (!parsed) {
        console.error('get-env-config returned no environment; skipping hash env write');
      } else {
        hashEnv = parsed;

        // Write under the channel-scoped key. `IRIS_ENV` is the renderer-facing
        // channel name set by the get-env-config IPC handler.
        localStorage.setItem(hashEnvKey(parsed.IRIS_ENV), JSON.stringify(parsed));

        SetEndpoint(parsed.IRIS_API_ENDPOINT);
      }
    } catch (e) {
      console.error('Failed to parse hash environment', e);
    }
  } else {
    // ENG-3760 (G4): browser path — read from the same channel-scoped key,
    // falling back to the legacy unscoped key for single-channel installs
    // that wrote before the migration. Vite exposes the channel as
    // `IRIS_ENV` (see studio/client/packages/frontend/src/vite.d.ts).
    // Optional-chain every `import.meta.env` read below: under jest
    // (--experimental-vm-modules) there is no Vite transform, so `import.meta.env`
    // is `undefined` and an unguarded read throws `Cannot read properties of
    // undefined (reading 'IRIS_ENV')` at load. `?.` is a no-op in a real Vite
    // build. Matches release/rtsp-assist PR #1209.
    // Fix: single-camera-in-multi-add fallback — CI test-studio jest-env repair.
    // Per rule: reference-rules-in-comments (ai.knowledge/global).
    const browserChannel = import.meta.env?.IRIS_ENV as string | undefined;
    const hash =
      localStorage.getItem(hashEnvKey(browserChannel)) ?? localStorage.getItem('__hash_env');

    if (hash) {
      try {
        const parsed = JSON.parse(hash);

        // Electron writes the full get-env-config payload, including the
        // packaged-app stamp, into this overlay. A later Chrome tab on the
        // same origin must not treat that stamp as this web bundle's build.
        // Electron still takes IRIS_APP_VERSION from IPC on the branch above.
        // Per rule: stay-in-scope (ai.knowledge/global) — browser path only.
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          delete (parsed as { IRIS_APP_VERSION?: unknown }).IRIS_APP_VERSION;
        }

        hashEnv = parsed;

        SetEndpoint(parsed.IRIS_API_ENDPOINT);
      } catch (e) {
        console.error('Failed to parse ls hash environment', e);
      }
    } else {
      console.log('No hash env found in local storage');
      const endpoint = import.meta.env?.IRIS_API_ENDPOINT;
      if (endpoint && endpoint !== '') {
        SetEndpoint(import.meta.env?.IRIS_API_ENDPOINT);
      }
    }
  }

  // Import the environment variables
  const env = {
    ...(import.meta.env ?? {}),
    ...hashEnv
  };

  // Parse the runtime configuration
  const config = toConfig({
    /** Defined in `.env` and `.env.{phase}` files. */

    IRIS_CDN_ENDPOINT: env.IRIS_CDN_ENDPOINT,
    IRIS_LIVEKIT_ENDPOINT:
      localStorage.getItem('__override.livekit_endpoint') ?? env.IRIS_LIVEKIT_ENDPOINT,
    IRIS_NATS_ENDPOINT: env.IRIS_NATS_ENDPOINT,
    IRIS_OAUTH_AUDIENCE: env.IRIS_OAUTH_AUDIENCE,
    IRIS_OAUTH_CLIENT_WEB: env.IRIS_OAUTH_CLIENT_WEB,
    IRIS_OAUTH_CLIENT_DESKTOP: env.IRIS_OAUTH_CLIENT_DESKTOP,
    IRIS_OAUTH_URL: env.IRIS_OAUTH_URL,
    IRIS_SENTRY_DSN: env.IRIS_SENTRY_DSN,

    IRIS_VENDOR_PTZOPTICS_OAUTH_AUDIENCE: env.IRIS_VENDOR_PTZOPTICS_OAUTH_AUDIENCE,
    IRIS_VENDOR_PTZOPTICS_OAUTH_CLIENT_WEB: env.IRIS_VENDOR_PTZOPTICS_OAUTH_CLIENT_WEB,
    IRIS_VENDOR_PTZOPTICS_OAUTH_CLIENT_DESKTOP: env.IRIS_VENDOR_PTZOPTICS_OAUTH_CLIENT_DESKTOP,
    IRIS_VENDOR_PTZOPTICS_OAUTH_URL: env.IRIS_VENDOR_PTZOPTICS_OAUTH_URL,

    IRIS_VENDOR: env.IRIS_VENDOR,

    IRIS_FRONTEND_URL: env.IRIS_FRONTEND_URL,

    IRIS_ELECTRON_VERSION: env.IRIS_ELECTRON_VERSION,

    /** Forwarded by the Electron main process via `get-env-config`; unset on web. */
    IRIS_APP_VERSION: env.IRIS_APP_VERSION,

    IRIS_API_ENDPOINT: env.IRIS_API_ENDPOINT,

    IRIS_ENV: env.IRIS_ENV,
    IRIS_POSTHOG_ENV: env.IRIS_POSTHOG_ENV,

    /** Defined in `vite.config.js`. typeof-guarded so jest can import this file. */
    IRIS_VERSION: typeof __STUDIO_VERSION__ !== 'undefined' ? __STUDIO_VERSION__ : '0.0.0-test',

    /** Defined by Vite */
    MODE: env.MODE,

    IRIS_BUILD_INFO_ALLOWED_EMAILS: env.IRIS_BUILD_INFO_ALLOWED_EMAILS,

    IRIS_NO_MEDIA_INPUT_SANDBOX: env.IRIS_NO_MEDIA_INPUT_SANDBOX
  });

  if (!config) {
    throw new StudioError('Unable to parse configuration.');
  }

  // Per rule: ui-architecture (ai.knowledge/global) — one validated vendor drives every palette.
  ThemeModule.setThemeVendor(config.vendor);

  return config;
});

/** Configuration proxy. */
export const config = new Proxy<Config>(<T_Any>{}, {
  get(...args): unknown {
    return Reflect.get(__config.value, args[1], args[2]);
  }
});
